import TimeStamp from "@/core/types/TimeStamp";

export enum TimerMode {
  NORMAL = 1000,
  PRECISE = 300
}

/**
 * Колбек который вызывается при каждом обновлении времени
 */
export type LiveTimeCallback = (currentTime: Date) => void;

export type LiveTimeType = {
  // Управление
  forceUpdate(): void;
  start(mode?: TimerMode): void;
  setMode(mode: TimerMode): void;
  stop(): void;

  // Подписки
  subscribe(callback: LiveTimeCallback): () => void;
  unsubscribe(callback: LiveTimeCallback): void;

  // Сдвиги от текущего момента
  addMilliseconds(milliseconds: number): TimeStamp;
  addMinutes(minutes: number): TimeStamp;
  addHours(hours: number): TimeStamp;
  addDays(days: number): TimeStamp;

  // Преобразования
  valueOf(): number;
  toString(): string;
  [Symbol.toPrimitive](hint: 'number' | 'string' | 'default'): number | string;
};

//      ╔═══════════════════════════════════════════════╗
//             __               _______
//            / /   (_)   _____/_  __(_)___ ___  ___
//           / /   / / | / / _ \/ / / / __ `__ \/ _ \
//          / /___/ /| |/ /  __/ / / / / / / / /  __/
//         /_____/_/ |___/\___/_/ /_/_/ /_/ /_/\___/
//
//      ╚═══════════════════════════════════════════════╝ "Wibbly wobbly, timey wimey..."
/**
 * LiveTime — реактивный источник текущего времени с системой подписок.
 *
 * Предоставляет единый синхронизированный источник времени для всего приложения.
 * Автоматически обновляется по таймеру и уведомляет подписчиков об изменениях.
 *
 * Два режима работы:
 * - NORMAL (1000ms) — для обычных задач (отображение часов)
 * - PRECISE (300ms) — для задач требующих точности
 *
 * Поддерживает приведение к примитивам — можно использовать напрямую в сравнениях:
 * ```ts
 * if (Core.liveTime < deadline) { ... }
 * console.log(`Сейчас: ${Core.liveTime}`);
 * ```
 *
 * @example Базовое использование
 * ```ts
 * // Доступ через Core
 * const now = Core.liveTime.toDateTime();
 *
 * // Проверки
 * if (Core.liveTime.isBefore(deadline)) {
 *   console.log('Ещё есть время');
 * }
 * ```
 *
 * @example Подписка на обновления
 * ```ts
 * // Подписка возвращает функцию отписки
 * const unsubscribe = Core.liveTime.subscribe((time) => {
 *   clockElement.textContent = time.toLocaleTimeString();
 * });
 *
 * // При размонтировании компонента
 * onUnmounted(() => unsubscribe());
 * ```
 *
 * @example Переключение режимов
 * ```ts
 * // Включить точный режим для критичной операции
 * Core.liveTime.setMode(TimerMode.PRECISE);
 *
 * // Вернуть обычный режим
 * Core.liveTime.setMode(TimerMode.NORMAL);
 * ```
 *
 * @see TimerMode — доступные режимы таймера
 * @see TimeStamp — тип внутреннего хранения времени
 */
export default class LiveTime  {
  private _now = ref(new TimeStamp());
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private mode: number;
  // ============ Система подписок ============
  private subscribers: Set<LiveTimeCallback> = new Set();

  constructor() {
    this.start();
  }

  // ============ Time calculations ============

  private updateNow(): void {
    this._now.value  = new TimeStamp();
  }

  /**
   * Уведомляет всех подписчиков о новом значении времени
   */
  private notifySubscribers(): void {
    const currentTime = this._now.value;

    this.subscribers.forEach(callback => {
      try {
        callback(currentTime);
      } catch {}
    });
  }


  // ============ Public API ============

  forceUpdate(): void {
    this.updateNow();
  }

  start(mode: TimerMode = TimerMode.NORMAL): void {
    if (this.intervalId) return;
    this.setMode(mode);
  }

  setMode(mode: TimerMode): void {
    this.mode = mode;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.updateNow();

    this.intervalId = setInterval(() => {
      this.updateNow();
      this.notifySubscribers()
    }, mode);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  toDateTime() {
    return this._now;
  }

  /**
   * Проверяет, раньше ли текущая дата
   * @param {DateSimple|Date} date - Другая дата
   * @returns {boolean}
   */
  isBefore(date: Date): boolean {
    return this._now.value.isBefore(date);
  }

  /**
   * Проверяет, позже ли текущая дата
   * @param {DateSimple|Date} date - Другая дата
   * @returns {boolean}
   */
  isAfter(date: Date): boolean {
    return this._now.value.isAfter(date);
  }

  /**
   * Проверяет равенство дат
   * @param {DateSimple|Date} date - Другая дата
   * @returns {boolean}
   */
  equals(date: Date) {
    return  this._now.value.compareTo(date) === 0;
  }


  // ============ Сдвиги от текущего момента ============

  /**
   * Момент через заданное число миллисекунд от текущего.
   *
   * Читает реактивное значение, поэтому результат живой: в шаблоне
   * пересчитывается на каждом тике и не требует computed-обёртки.
   *
   * @example
   * // «когда начнутся торги, если создать их сейчас»
   * $core.liveTime.addMilliseconds(TendersService.config.CREATION_DELAY_MS)
   */
  addMilliseconds(milliseconds: number): TimeStamp {
    return this._now.value.addMilliseconds(milliseconds);
  }

  /**
   * Момент через заданное число минут от текущего.
   *
   * @example
   * $core.liveTime.addMinutes(20)
   */
  addMinutes(minutes: number): TimeStamp {
    return this._now.value.addMinutes(minutes);
  }

  /**
   * Момент через заданное число часов от текущего.
   *
   * @example
   * $core.liveTime.addHours(-3)
   */
  addHours(hours: number): TimeStamp {
    return this._now.value.addHours(hours);
  }

  /**
   * Момент через заданное число дней от текущего.
   *
   * @example
   * $core.liveTime.addDays(7)
   */
  addDays(days: number): TimeStamp {
    return this._now.value.addDays(days);
  }


  // ============ Подписки ============

  /**
   * Подписаться на обновления времени
   *
   * @param callback - Функция которая вызывается при каждом тике
   * @returns Функция для отписки
   *
   * @example
   * const unsubscribe = Core.time.subscribe((time) => {
   *   console.log('Tick:', time);
   * });
   *
   * // Позже отписаться
   * unsubscribe();
   */
  subscribe(callback: LiveTimeCallback): () => void {
    this.subscribers.add(callback);

    // Возвращаем функцию отписки
    return () => this.unsubscribe(callback);
  }

  /**
   * Отписаться от обновлений
   */
  unsubscribe(callback: LiveTimeCallback): void {
    this.subscribers.delete(callback);
  }

  /**
   * Очистить все подписки (для тестов/отладки)
   */
  clearSubscribers(): void {
    this.subscribers.clear();
  }

  /**
   * Количество активных подписчиков
   */
  get subscribersCount(): number {
    return this.subscribers.size;
  }


  get isNormalMode() {
    return this.mode === TimerMode.NORMAL;
  }

  get isPreciseMode() {
    return this.mode === TimerMode.PRECISE;
  }

  // ============ Магия: работа как примитив ============

  /**
   * Позволяет использовать как примитив в сравнениях
   */
  [Symbol.toPrimitive](hint: string): number | string {
    if (hint === 'number') {
      return this._now.value.getTime();
    }
    if (hint === 'string') {
      return this._now.value.toString();
    }
    return this._now.value.getTime();
  }

  /**
   * Возвращает timestamp при приведении к числу
   * оставлен в качестве преемственности
   */
  valueOf(): number {
    return this._now.value.getTime();
  }

  /**
   * Возвращает ISO строку при приведении к строке
   * оставлен в качестве преемственности
   */
  toString(): string {
    return this._now.value.toISOString();
  }


}
